//https://stackoverflow.com/questions/41030361/how-to-update-react-context-from-inside-a-child-component

import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import {
  IUserPreferences,
  IUserSession,
  ISessionToken,
  Timestamp,
} from "../types";
import { firebaseAuth } from "../firebaseConfig";
import { onIdTokenChanged, IdTokenResult } from "firebase/auth";
import User from "../models/User";

interface UserContextProps {
  userSession: IUserSession | undefined;
  createUserSession: (user: any) => IUserSession | undefined;
  isAuthenticated: () => boolean;
  revokeUserSession: () => void;
  setUserSession: (userSession: IUserSession | undefined) => void;
  persistUserSession: (session: IUserSession) => void;
  updateUserPreferences: (
    preferences: Partial<IUserPreferences>,
  ) => Promise<void>;
  authReady: boolean;
}

const UserContext = createContext<UserContextProps>(undefined!);

export function UserProvider({ children }: PropsWithChildren) {
  const [userSession, setUserSession] = useState(
    window.localStorage.getItem("userSession")
      ? (JSON.parse(
          window.localStorage.getItem("userSession") ?? "{}",
        ) as IUserSession)
      : undefined,
  );
  const [authReady, setAuthReady] = useState(false);

  // Listen for automatic ID-token refreshes and keep our session copy in sync
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(firebaseAuth, async (user) => {
      if (!user) {
        setUserSession(undefined);
        setAuthReady(true);
        return;
      }

      // Don't automatically create session during signup flow - let signup page handle it
      if (window.location.pathname === "/signup") {
        setAuthReady(true);
        return;
      }

      // Retrieve a fresh token (after silent refresh or initial sign-in)
      const tokenResult: IdTokenResult = await user.getIdTokenResult();

      // Preserve existing admin flag (derived from Firestore) so it is not
      // overwritten by automatic token refreshes. We use a functional state
      // update to always access the latest stored session.
      setUserSession((prev) => {
        const isAdmin = prev?.user.isAdmin ?? false;

        return {
          token: {
            accessToken: tokenResult.token,
            refreshToken: user.refreshToken,
            // tokenResult.expirationTime is an ISO string – convert to ms epoch
            expirationTime: new Date(tokenResult.expirationTime).getTime(),
          },
          user: {
            id: user.email ?? "",
            email: user.email ?? "",
            displayName: user.displayName,
            userId: user.uid,
            emailVerified: user.emailVerified,
            isAdmin,
            createdAt: Timestamp.now(),
            preferences: prev?.user.preferences,
          },
        } as IUserSession;
      });
      setAuthReady(true);
    });

    return unsubscribe;
  }, []);

  // Save latest state to localStorage
  useEffect(() => {
    if (userSession) {
      window.localStorage.setItem("userSession", JSON.stringify(userSession));
    } else {
      window.localStorage.removeItem("userSession");
    }
  }, [userSession]);

  function isAuthenticated(): boolean {
    return !!firebaseAuth.currentUser;
  }

  function createUserSession(user: any): IUserSession | undefined {
    if (user) {
      return {
        token: user.stsTokenManager as ISessionToken,
        user: {
          id: user.email,
          email: user.email,
          displayName: user.displayName,
          userId: user.uid,
          emailVerified: user.emailVerified,
          isAdmin: user.isAdmin,
          createdAt: Timestamp.now(),
        },
      };
    }
    return undefined;
  }

  function persistUserSession(session: IUserSession) {
    window.localStorage.setItem("userSession", JSON.stringify(session));
  }

  function revokeUserSession() {
    window.localStorage.removeItem("userSession");
    window.localStorage.removeItem("openSidebarDropdowns");
    // Clear the persisted active workspace so the next user in this browser
    // does not start with the previous user's tenant context.
    window.localStorage.removeItem("workspace");
  }

  async function updateUserPreferences(
    updates: Partial<IUserPreferences>,
  ): Promise<void> {
    if (!userSession?.user.email) return;

    const email = userSession.user.email;
    setUserSession((currentSession) => {
      if (!currentSession || currentSession.user.email !== email) {
        return currentSession;
      }
      return {
        ...currentSession,
        user: {
          ...currentSession.user,
          preferences: {
            ...currentSession.user.preferences,
            ...updates,
          },
        },
      };
    });

    try {
      await User.updatePreferences(email, updates);
    } catch (error) {
      // The browser cache remains authoritative for the current device even if
      // profile synchronization is temporarily unavailable.
      console.error("Failed to save user preferences:", error);
    }
  }

  return (
    <UserContext.Provider
      value={{
        userSession: userSession,
        createUserSession: createUserSession,
        isAuthenticated: isAuthenticated,
        revokeUserSession: revokeUserSession,
        setUserSession: setUserSession,
        persistUserSession: persistUserSession,
        updateUserPreferences,
        authReady: authReady,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUserSession(): UserContextProps {
  const context = useContext(UserContext);

  if (typeof context === "undefined") {
    throw new Error("useUser should be used within the UserContext provider!");
  }

  return context;
}
